import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { KeySquare } from 'lucide-react';
import {
  getSecretsAsync,
  selectSecrets,
  selectSecretsLoading,
  selectSecretsError,
  selectSecretsCount
} from '../../redux/getSecrets/reducer';
import { InteractionRequiredAuthError } from '@azure/msal-common';
import { useTranslation } from 'react-i18next';
import { AppDispatch } from '../../redux/store';
import SecretDetail from '../SecretDetail';
import UpdateSecret from '../SecretUpdate';
import '../../styles/css/secret.css';
import brand from "../../brand";
import "../../styles/css/spinner.css";

const SystemCredentials = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const secrets = useSelector(selectSecrets);
  const secretsCount = useSelector(selectSecretsCount);
  const loading = useSelector(selectSecretsLoading);
  const error = useSelector(selectSecretsError);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const [selectedSecretName, setSelectedSecretName] = useState<string | null>(null);
  const [updateSecretName, setUpdateSecretName] = useState<string | null>(null);

  useEffect(() => {
    const fetchSecrets = async () => {
      try {
        await dispatch(getSecretsAsync()).unwrap();
      } catch (err: any) {
        console.error('Error fetching secrets:', err);
        if (err instanceof InteractionRequiredAuthError ||
            (err?.name === 'InteractionRequiredAuthError') ||
            (typeof err === 'string' && err.includes('Authentication'))) {
          setSessionExpired(true);
        }
      }
    };

    fetchSecrets();
  }, [dispatch]);

  const handleViewSecret = (secretName: string) => {
    setSelectedSecretName(secretName);
  };

  const handleRequestUpdate = () => {
    setUpdateSecretName('general');
  };

  const handleCloseUpdateModal = () => {
    setUpdateSecretName(null);
    dispatch(getSecretsAsync());
  };

  const handleBackToList = () => {
    setSelectedSecretName(null);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    if (!dateString.includes('/')) {
      return dateString;
    }
    const parts = dateString.split('/');
    return `${parts[0]}-${parts[1]}-${parts[2]}`;
  };

  if (selectedSecretName) {
    return <SecretDetail secretName={selectedSecretName} onBack={handleBackToList} />;
  }

  if (loading && secrets.length === 0) {
    return (
      <div className="spinner">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    );
  }

  if (sessionExpired) {
    return (
      <div className="error-container">
        <h3>Session Expired</h3>
        <p>Your session has expired or your token is invalid. Please refresh the page to continue.</p>
        <button onClick={handleRefresh} className="refresh-button">
          Refresh Page
        </button>
      </div>
    );
  }

  return (
    <div className="system-credentials-container">
      <div className="credentials-header">
        <div className="title-wrapper">
          <KeySquare className="title-icon secondaryColor"/>
          <h2 className="system-credentials-title secondaryColor" style={{fontFamily: brand.titleFont}}>{t('System Credentials')}</h2>
        </div>
        <button onClick={handleRequestUpdate} className="request-update-btn request-update-btn-text-color " >
          {t('Request Update')}
        </button>
      </div>
      <p className="system-credentials-count" style={{fontFamily: brand.bodyFont}}>{secretsCount} {t('secrets found')}</p>

      <table className="secrets-table" style={{fontFamily: brand.bodyFont}}>
        <thead>
          <tr>
            <th>{t('Secret Name')}</th>
            <th>{t('Expiry Date')}</th>
            <th>{t('ARN')}</th>
            <th>{t('Details')}</th>
          </tr>
        </thead>
        <tbody>
          {secrets && secrets.length > 0 ? (
            secrets.map((secret) => (
              <tr key={secret.arn}>
                <td>
                  {secret.secret_name}
                </td>
                <td>
                  {formatDate(secret.expiry_date)}
                  {(secret?.expiry_date.includes('Never') || secret?.daysUntilExpiry !== undefined || secret?.daysExpired !== undefined) && (
                    <span className={`status-badge ${secret.isExpired ? 'invalid' : 'valid'}`}>
                      {secret.isExpired ? t('Invalid') : t('Valid')}
                    </span>
                  )}
                </td>
                <td>
                  <span title={secret.arn}>{secret.arn}</span>
                </td>
                <td>
                  <button
                    onClick={() => handleViewSecret(secret.secret_name)}
                    className="view-details-btn secondaryColor"
                  >
                    {t('View Details')}
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} style={{textAlign: 'center'}}>
                No credentials found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {updateSecretName && (
        <UpdateSecret
          onClose={handleCloseUpdateModal}
        />
      )}
    </div>
  );
};

export default SystemCredentials;